import type { JSX } from 'react';
import { useI18n } from '../../i18n';
import type { CheckResult } from '../../lib/types';
import { cx } from './cx';
import { toneClass } from './tone';

/**
 * One dense telemetry line: status dot, domain, response time. A port of
 * `android/.../ui/components/CompactResultRow.kt`, used for the "recent" list
 * on the dashboard.
 *
 * ### There is no HTTP-code column, and its absence is the point
 *
 * Android renders `result.statusCode` between the domain and the time. On this
 * platform `statusCode` is **always** null: the probe is a `no-cors` fetch and a
 * `no-cors` response reports `0`, so there is no status to show and nothing that
 * could be derived into one (SPEC §6, CLAUDE.md). The column is removed rather
 * than left empty — a slot that is always blank reads as a number that happened
 * to be missing *this time*, which is a worse claim than saying nothing.
 */
export function CompactResultRow({ result }: { result: CheckResult }): JSX.Element {
  const { t, n } = useI18n();
  const url = result.link.url;
  const split = splitForMiddleEllipsis(url);

  return (
    <div className="compact-row">
      {/* Three states, not Android's two. The dot's colour comes from
          `toneClass`, shared with the Results tile: the same link appearing
          amber in one list and red in the other would look like a bug in the
          data rather than two views of one row. */}
      <span className={cx('compact-row__dot', toneClass(result))} />
      {split === null ? (
        <span className="compact-row__url t-body-medium clamp-1">{url}</span>
      ) : (
        <span className="compact-row__url compact-row__url--split t-body-medium clamp-1">
          <span className="compact-row__url-head clamp-1">{split.head}</span>
          <span className="compact-row__url-tail">{split.tail}</span>
        </span>
      )}
      <span className="compact-row__ms n-small">
        {/*
          Android shows `value_none` when `responseMs` is null. There is no null
          here — `ProbeOutcome.responseMs` is always a number — but a plain
          `http://` link is rejected before any fetch happens and carries a
          measured time of zero. "0 ms" would read as an impossibly fast site;
          the em dash reads as what it is, no measurement.
        */}
        {result.responseMs > 0 ? t('unit_ms', n(result.responseMs)) : t('value_none')}
      </span>
    </div>
  );
}

/** Characters kept on the right of the split. Enough for a label and a TLD. */
const URL_TAIL_CHARS = 12;

/** Below this there is nothing worth splitting; the whole URL fits anyway. */
const URL_MIN_SPLIT_CHARS = 20;

/**
 * Android uses `TextOverflow.MiddleEllipsis` so the **TLD survives** —
 * "very-long-subdomain…/path" tells you nothing, while "very-long…88.com" still
 * identifies the link, and identifying the link is the row's whole job.
 *
 * CSS has no middle ellipsis. What it has is `text-overflow: ellipsis` on a
 * shrinking element, so the string is split here and rendered as two spans: a
 * head that shrinks and ellipsises, and a tail that never shrinks
 * (`.compact-row__url-head` / `__url-tail`). The visible result is Android's,
 * including the part that matters most — when there *is* room, nothing is
 * elided at all, because the head simply never overflows.
 *
 * Split on code points rather than UTF-16 units. A Chinese IDN domain sliced
 * mid-surrogate renders as a replacement character, which would corrupt exactly
 * the domains this app exists to watch in exactly the market it was written for.
 */
function splitForMiddleEllipsis(url: string): { head: string; tail: string } | null {
  const chars = Array.from(url);
  if (chars.length < URL_MIN_SPLIT_CHARS) return null;
  return {
    head: chars.slice(0, chars.length - URL_TAIL_CHARS).join(''),
    tail: chars.slice(chars.length - URL_TAIL_CHARS).join(''),
  };
}
